import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Wind, Fan, Filter, Activity, Phone } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import img from "../Images/pollution.jpg";
import img2 from "../Images/cyc.jpg";
import img3 from "../Images/fans.jpg";
import img1 from "../Images/dust.jpg";
import img4 from "../Images/dust.png";

const PollutionControl = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const equipmentTypes = {
    bagFilter: {
      title: "Bag Filters",
      icon: <Filter className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "High-efficiency fabric filtration systems designed for capturing fine particulate matter from industrial gas streams.",
      image: img1,
      features: [
        "Pulse-jet cleaning mechanism",
        "High collection efficiency (99.9%)",
        "Temperature-resistant filter media",
        "Continuous monitoring system",
        "Modular design for easy maintenance",
        "Advanced cage design for better support"
      ],
      specifications: [
        "Air-to-cloth ratio: 2.4:1 to 4:1",
        "Operating temperature: up to 260°C",
        "Pressure drop range: 100-150 mmWC",
        "Filter media options: Polyester/Nomex/PTFE",
        "Particulate removal: down to 0.5 micron"
      ],
      applications: [
        "Cement plants",
        "Steel industries",
        "Power plants",
        "Chemical processing",
        "Food processing industries"
      ]
    },
    cyclone: {
      title: "Cyclones",
      icon: <Wind className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Mechanical separators utilizing centrifugal force for efficient removal of particulate matter from gas streams.",
      image: img2,
      features: [
        "High-efficiency cyclonic separation",
        "No moving parts design",
        "Low maintenance requirements",
        "Compact installation footprint",
        "Multiple cyclone arrangement option",
        "Robust construction for durability"
      ],
      specifications: [
        "Collection efficiency: 85-95%",
        "Temperature tolerance: up to 400°C",
        "Pressure drop: 50-100 mmWC",
        "Particle size range: 5-200 microns",
        "Flow capacity: up to 100,000 m³/hr"
      ],
      applications: [
        "Pre-cleaning in industrial processes",
        "Wood processing industry",
        "Agricultural processing",
        "Mining operations",
        "Pharmaceutical industry"
      ]
    },
    fans: {
      title: "Industrial Fans",
      icon: <Fan className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "High-performance industrial fans designed for efficient air movement in pollution control systems.",
      image: img3,
      features: [
        "Backward curved impeller design",
        "Variable frequency drive compatibility",
        "Vibration monitoring system",
        "Aerodynamic blade profile",
        "Heavy-duty bearing assembly",
        "Sound attenuation options"
      ],
      specifications: [
        "Capacity range: 1,000-500,000 m³/hr",
        "Static pressure: up to 1000 mmWC",
        "Temperature rating: up to 350°C",
        "Motor power: up to 1000 kW",
        "Efficiency: up to 85%"
      ],
      applications: [
        "Exhaust gas handling",
        "Ventilation systems",
        "Process gas movement",
        "Cooling systems",
        "Fume extraction"
      ]
    },
    dustCollector: {
      title: "Dust Collectors",
      icon: <Activity className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Advanced dust collection systems for maintaining clean air quality in industrial environments.",
      image: img4,
      features: [
        "Automated cleaning system",
        "Smart monitoring capabilities",
        "Energy-efficient operation",
        "Easy filter replacement",
        "Explosion protection options",
        "Multiple inlet configurations"
      ],
      specifications: [
        "Airflow capacity: up to 100,000 CFM",
        "Filtration efficiency: 99.99%",
        "Filter life: 2-5 years",
        "Sound level: <85 dBA",
        "Power consumption: 0.5-2.0 kW/1000 CFM"
      ],
      applications: [
        "Metalworking operations",
        "Woodworking facilities",
        "Pharmaceutical production",
        "Food processing",
        "Chemical manufacturing"
      ]
    }
  };

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Pollution Control Equipment & Solutions",
    "description": "Comprehensive air pollution control systems including bag filters, cyclones, industrial fans, and dust collectors",
    "itemListElement": Object.entries(equipmentTypes).map(([key, equipment], index) => ({
      "@type": "Product",
      "position": index + 1,
      "name": equipment.title,
      "description": equipment.description,
      "image": equipment.image,
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Gortherm Energy"
        }
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Pollution Control Equipment & Air Filtration Systems | Gortherm Energy</title>
        <meta name="description" content="Industrial pollution control solutions including bag filters, cyclones, dust collectors, and industrial fans. High-efficiency air filtration systems for cleaner industrial operations. Expert pollution control equipment manufacturer." />
        <meta name="keywords" content="pollution control equipment, bag filters, cyclone separators, dust collectors, industrial fans, air filtration systems, emission control, particulate removal, environmental compliance, industrial air quality, pollution control Nepal" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Pollution Control Equipment & Air Filtration Systems | Gortherm Energy" />
        <meta property="og:description" content="Advanced pollution control technologies for cleaner air and sustainable industrial operations. Bag filters, cyclones, dust collectors, and industrial fans." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/pollution-control" />
        <meta property="og:image" content={img} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pollution Control Equipment | Gortherm Energy" />
        <meta name="twitter:description" content="High-efficiency pollution control systems for industrial air quality management." />
        <meta name="twitter:image" content={img} />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/pollution-control" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Hero Section */}
        <motion.header
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative h-[300px] sm:h-[400px] lg:h-[500px] overflow-hidden"
        >
          <img
            src={img}
            alt="Advanced pollution control solutions and air filtration systems by Gortherm Energy"
            className="w-full h-full object-cover"
            loading="eager"
            width="1920"
            height="500"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="max-w-2xl"
              >
                <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tight mb-4 sm:mb-6 leading-tight">
                  Pollution Control
                  <span className="text-green-400"> Solutions</span>
                </h1>
                <p className="text-base sm:text-lg lg:text-xl text-gray-200 mb-6 sm:mb-8">
                  Advanced technologies for cleaner air and sustainable industrial operations
                </p>
              </motion.div>
            </div>
          </div>
        </motion.header>

        {/* Equipment Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 sm:mb-12">
            Pollution Control Equipment
          </h2>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8"
          >
            {Object.entries(equipmentTypes).map(([key, equipment], idx) => (
              <motion.article
                key={key}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                onHoverStart={() => setHoveredCard(idx)}
                onHoverEnd={() => setHoveredCard(null)}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
                itemScope
                itemType="https://schema.org/Product"
              >
                <div className="relative h-48 sm:h-56 md:h-64">
                  <img
                    src={equipment.image}
                    alt={`${equipment.title} - Industrial air pollution control equipment`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="600"
                    height="400"
                    itemProp="image"
                  />
                </div>
                <div className="p-4 sm:p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="text-green-600" aria-hidden="true">
                      {equipment.icon}
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900" itemProp="name">
                      {equipment.title}
                    </h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 mb-4" itemProp="description">
                    {equipment.description}
                  </p>
                  <button
                    onClick={() => setExpandedSection(expandedSection === idx ? null : idx)}
                    className="flex items-center space-x-2 text-green-600 hover:text-green-700 transition-colors text-sm sm:text-base font-medium"
                    aria-expanded={expandedSection === idx}
                    aria-controls={`details-${idx}`}
                  >
                    <span>Learn more</span>
                    <motion.div
                      animate={{ rotate: expandedSection === idx ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {expandedSection === idx && (
                      <motion.div
                        id={`details-${idx}`}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 space-y-4"
                      >
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                            Key Features
                          </h4>
                          <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-gray-600">
                            {equipment.features.map((feature, i) => (
                              <li key={i}>{feature}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                            Technical Specifications
                          </h4>
                          <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-gray-600">
                            {equipment.specifications.map((spec, i) => (
                              <li key={i}>{spec}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                            Applications
                          </h4>
                          <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-gray-600">
                            {equipment.applications.map((app, i) => (
                              <li key={i}>{app}</li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </section>

        {/* Why Choose Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">
              Why Choose Our Pollution Control Solutions?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 text-gray-700">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-green-600">
                  Environmental Compliance
                </h3>
                <p className="text-sm sm:text-base">
                  Our pollution control systems meet and exceed international environmental standards, 
                  ensuring your facility maintains compliance with air quality regulations.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-green-600">
                  High Efficiency
                </h3>
                <p className="text-sm sm:text-base">
                  Advanced filtration technologies achieve up to 99.99% particulate removal efficiency, 
                  protecting equipment and ensuring clean air discharge.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-green-600">
                  Cost-Effective Operation
                </h3>
                <p className="text-sm sm:text-base">
                  Energy-efficient designs and low maintenance requirements reduce operational costs 
                  while maximizing system uptime and performance.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-green-600">
                  Custom Solutions
                </h3>
                <p className="text-sm sm:text-base">
                  Tailored pollution control systems designed to meet your specific industrial 
                  requirements, facility layout, and emission control objectives.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <aside className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Ready to Improve Your Air Quality?
            </h2>
            <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Our pollution control experts are ready to help you design and implement 
              the perfect air filtration solution for your facility.
            </p>
            <a 
              href="tel:+9779802781400"
              className="inline-block"
              aria-label="Contact pollution control experts at +977 980 278 1400"
            >
              <button className="bg-green-600 hover:bg-green-700 active:bg-green-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold">
                <Phone className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                <span>Contact Our Experts</span>
              </button>
            </a>
            
          </div>
        </aside>
      </main>
    </>
  );
};

export default PollutionControl;